import React, { useState, useEffect, useRef } from 'react';
import { FaCloudUploadAlt, FaPlus, FaTimes } from 'react-icons/fa';
import { FiEdit2, FiTrash2, FiUpload } from 'react-icons/fi';
import { message, Switch } from 'antd';
import AdminService from '../services/adminService';
import { config } from '../utils/config';

const SyncGearIcon = ({ size = "1em", color = "currentColor" }) => (
  <svg stroke={color} fill={color} strokeWidth="0" viewBox="0 0 512 512" height={size} width={size} xmlns="http://www.w3.org/2000/svg">
    <path d="M370.72 133.28C339.458 104.008 298.888 87.962 255.848 88c-77.458.068-144.328 53.178-162.791 126.85-1.344 5.363-6.122 9.15-11.651 9.15H24.103c-7.498 0-13.194-6.807-11.807-14.176C33.933 94.924 134.813 8 256 8c66.448 0 126.791 26.136 171.315 68.685L463.03 40.97C478.149 25.851 504 36.559 504 57.941V192c0 13.255-10.745 24-24 24H345.941c-21.382 0-32.09-25.851-16.971-40.971l41.75-41.749zM32 296h134.059c21.382 0 32.09 25.851 16.971 40.971l-41.75 41.75c31.262 29.273 71.835 45.319 114.876 45.28 77.418-.07 144.315-53.144 162.787-126.849 1.344-5.363 6.122-9.15 11.651-9.15h57.304c7.498 0 13.194 6.807 11.807 14.176C478.067 417.076 377.187 504 256 504c-66.448 0-126.791-26.136-171.315-68.685L48.97 471.03C33.851 486.149 8 475.441 8 454.059V320c0-13.255 10.745-24 24-24z"></path>
    <g transform="matrix(0.55 0 0 0.55 115.2 115.2)">
      <path d="M487.4 315.7l-42.6-24.6c4.3-23.2 4.3-47 0-70.2l42.6-24.6c4.9-2.8 7.1-8.6 5.5-14-11.1-35.6-30-67.8-54.7-94.6-3.8-4.1-10-5.1-14.8-2.3L380.8 110c-17.9-15.4-38.5-27.3-60.8-35.1V25.8c0-5.6-3.9-10.5-9.4-11.7-36.7-8.2-74.3-7.8-109.2 0-5.5 1.2-9.4 6.1-9.4 11.7V75c-22.2 7.9-42.8 19.8-60.8 35.1L88.7 85.5c-4.9-2.8-11-1.9-14.8 2.3-24.7 26.7-43.6 58.9-54.7 94.6-1.7 5.4.6 11.2 5.5 14L67.3 221c-4.3 23.2-4.3 47 0 70.2l-42.6 24.6c-4.9 2.8-7.1 8.6-5.5 14 11.1 35.6 30 67.8 54.7 94.6 3.8 4.1 10 5.1 14.8 2.3l42.6-24.6c17.9 15.4 38.5 27.3 60.8 35.1v49.2c0 5.6 3.9 10.5 9.4 11.7 36.7 8.2 74.3 7.8 109.2 0 5.5-1.2 9.4-6.1 9.4-11.7v-49.2c22.2-7.9 42.8-19.8 60.8-35.1l42.6 24.6c4.9 2.8 11 1.9 14.8-2.3 24.7-26.7 43.6-58.9 54.7-94.6 1.5-5.5-.7-11.3-5.6-14.1zM256 336c-44.1 0-80-35.9-80-80s35.9-80 80-80 80 35.9 80 80-35.9 80-80 80z"></path>
    </g>
  </svg>
);

const UNIT_OPTIONS = [
  'None', 'pcs', 'Plate', 'Portion', 'Serving', 'Cup', 'Glass', 'Bowl', 'Slice',
  'g', 'kg', 'ml', 'l', 'pack', 'Packet', 'Bottle', 'Can', 'Box', 'Other'
];

const escapeRegex = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const cleanVariantBaseValue = (val, currentUnit) => {
  if (!val) return '';
  let str = String(val).trim();

  // 1. If currentUnit is provided and not 'none', strip it if at end
  if (currentUnit && currentUnit.trim() && currentUnit.toLowerCase() !== 'none') {
    const u = currentUnit.trim();
    const regex = new RegExp(`\\s+${escapeRegex(u)}$`, 'i');
    if (regex.test(str)) {
      const cand = str.replace(regex, '').trim();
      if (cand) str = cand;
    }
  }

  // 2. Check against UNIT_OPTIONS (skip single letters like 'l' unless length > 1)
  for (const unit of UNIT_OPTIONS) {
    if (unit && unit.toLowerCase() !== 'none' && unit.toLowerCase() !== 'other' && unit.length > 1) {
      const regex = new RegExp(`\\s+${escapeRegex(unit)}$`, 'i');
      if (regex.test(str)) {
        const cand = str.replace(regex, '').trim();
        if (cand) {
          str = cand;
          break;
        }
      }
    }
  }

  return str;
};

const formatVariantSizeLabel = (val, unit) => {
  const base = cleanVariantBaseValue(val, unit);
  const u = (unit || '').trim();
  if (base && u && u.toLowerCase() !== 'none') {
    return `${base} ${u}`;
  }
  return base || (u && u.toLowerCase() !== 'none' ? u : 'Regular');
};

const EditMenuItemModal = ({ itemId, businessId, businessData, onClose, onSave, onRefresh, isSaving }) => {
  const [formData, setFormData] = useState({
    item_name: '',
    sku: '',
    category: '',
    sub_category: '',
    item_type: 'Veg',
    preparation_time: '',
    description: '',
    status: '1',
    is_visible: true,
    is_featured: false,
    is_customizable: false,
    is_organic: false,
    is_visible_counter: true,
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [apiCategories, setApiCategories] = useState([]);
  const [variants, setVariants] = useState([]);
  const [taxSlabs, setTaxSlabs] = useState([]);
  const [availabilityTimings, setAvailabilityTimings] = useState([]);
  const [useBusinessHours, setUseBusinessHours] = useState(true);
  const [businessHoursData, setBusinessHoursData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [updatingVariantId, setUpdatingVariantId] = useState(null);
  const [showNewVariantRow, setShowNewVariantRow] = useState(false);
  const [newVariant, setNewVariant] = useState({ size_label: '', unit: '', pos_plu: '', selling_price: '', original_cost: '', stock_qty: '', gst: '2', is_active: true, is_visible: true, is_visible_counter: true });
  const [isAddingVariant, setIsAddingVariant] = useState(false);
  const [deletingVariantId, setDeletingVariantId] = useState(null);
  const [confirmDeleteVariantId, setConfirmDeleteVariantId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const rawHours = businessData?.business_info?.business_hours || businessData?.business_hours;
    if (rawHours) {
      setBusinessHoursData(rawHours);
    } else if (businessId) {
      AdminService.getBusinessDetailedView(businessId)
        .then((res) => {
          const hours = res?.data?.business_info?.business_hours || res?.business_info?.business_hours || res?.data?.business_hours || res?.business_hours;
          if (hours) {
            setBusinessHoursData(hours);
          }
        })
        .catch((err) => {
          console.warn('Failed to load business hours:', err);
        });
    }
  }, [businessId, businessData]);

  useEffect(() => {
    const fetchTaxSlabs = async () => {
      try {
        const response = await AdminService.getTaxSlabs();
        if (response && response.success && response.data) {
          setTaxSlabs(response.data);
        } else if (Array.isArray(response)) {
          setTaxSlabs(response);
        }
      } catch (error) {
        console.error('Failed to load tax slabs:', error);
      }
    };
    fetchTaxSlabs();
  }, []);

  useEffect(() => {
    if (!businessId) return;
    const fetchCategories = async () => {
      try {
        const response = await AdminService.getBusinessCategories(businessId);
        if (response && response.success && response.data) {
          setApiCategories(response.data);
        } else if (Array.isArray(response)) {
          setApiCategories(response);
        }
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };
    fetchCategories();
  }, [businessId]);

  useEffect(() => {
    if (!itemId) return;
    const fetchItemDetails = async () => {
      try {
        const response = await AdminService.getMenuItem(itemId);
        if (response && response.success && response.data) {
          const item = response.data;
          setFormData({
            item_name: item.item_name || '',
            sku: item.sku || '',
            category: item.category_name || item.item_category || '',
            sub_category: item.sub_category_name || item.sub_category || '',
            item_type: item.item_type || 'Veg',
            preparation_time: item.preparation_time || '',
            description: item.description || '',
            status: String(item.status) || '1',
            is_visible: item.is_visible !== undefined ? Boolean(item.is_visible) : true,
            is_featured: item.is_featured !== undefined ? Boolean(item.is_featured) : false,
            is_customizable: item.is_customizable !== undefined ? Boolean(item.is_customizable) : false,
            is_organic: item.is_organic !== undefined ? Boolean(item.is_organic) : false,
            is_visible_counter: item.is_visible_counter !== undefined ? Boolean(item.is_visible_counter) : true,
          });
          if (item.variants && Array.isArray(item.variants)) {
            setVariants(item.variants.map(v => {
              const parsedUnit = v.unit || (typeof v.size_label === 'object' && v.size_label !== null ? v.size_label.unit : '') || v.net_weight_unit || '';

              const rawSizeLabel = typeof v.size_label === 'object' && v.size_label !== null
                ? (v.size_label.value !== undefined ? v.size_label.value : (v.size_label.label || v.size_label.size_label || ''))
                : (v.value || v.size_label || '');

              const cleanBase = cleanVariantBaseValue(rawSizeLabel, parsedUnit);

              return {
                ...v,
                size_label: cleanBase || rawSizeLabel,
                unit: parsedUnit,
                pos_plu: v.pos_plu || v.plu_code || v.plu || '',
                is_visible: v.is_visible !== false,
                is_visible_counter: (v.is_visible_counter !== undefined ? v.is_visible_counter : v.is_visible_at_counter) !== false,
              };
            }));
          }

          let normalizedTimings = [];
          const rawAvailabilityTimings = item.availability_timings || item.availabilityTimings || item.availabilityTiming || item.availability_timing;
          if (rawAvailabilityTimings) {
            try {
              const parsed = typeof rawAvailabilityTimings === 'string'
                ? JSON.parse(rawAvailabilityTimings)
                : rawAvailabilityTimings;

              if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                normalizedTimings = Object.entries(parsed).flatMap(([day, times]) => {
                  const dayValue = String(day).toLowerCase().slice(0, 3);
                  if (Array.isArray(times)) {
                    return times.map((slot, index) => ({
                      id: `${itemId}-${dayValue}-${index}`,
                      day: dayValue,
                      start: slot?.open || slot?.start || '09:00',
                      end: slot?.close || slot?.end || '21:00',
                    }));
                  }
                  return [{
                    id: `${itemId}-${dayValue}-0`,
                    day: dayValue,
                    start: times?.open || times?.start || '09:00',
                    end: times?.close || times?.end || '21:00',
                  }];
                });
                setAvailabilityTimings(normalizedTimings);
              }
            } catch (parseError) {
              console.warn('Failed to parse availability timings:', parseError);
            }
          }

          const rawUseBusinessHours = item.use_business_hours !== undefined ? item.use_business_hours : item.use_business_timing;
          if (rawUseBusinessHours !== undefined && rawUseBusinessHours !== null) {
            setUseBusinessHours(Boolean(rawUseBusinessHours === true || rawUseBusinessHours === 'true' || rawUseBusinessHours === 1 || rawUseBusinessHours === '1'));
          } else {
            if (normalizedTimings.length > 0) {
              setUseBusinessHours(false);
            } else {
              setUseBusinessHours(true);
            }
          }

          if (item.item_image) {
            let imageUrl = item.item_image;
            if (imageUrl) {
              imageUrl = imageUrl.replace('/media/media/', '/media/');
              imageUrl = imageUrl.replace(/%2520/g, '%20');
              if (!imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
                imageUrl = `${config.API_BASE_URL}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
              }
            }
            setImagePreview(imageUrl);
          }
        } else {
          message.error('Failed to fetch item details');
        }
      } catch (error) {
        console.error('Failed to load item:', error);
        message.error('Failed to load item details');
      } finally {
        setIsLoading(false);
      }
    };
    fetchItemDetails();
  }, [itemId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      if (name === 'category') {
        newData.sub_category = ''; // reset sub category when category changes
      }
      return newData;
    });
  };

  const handleVariantChange = (variantId, field, value) => {
    setVariants(prev => prev.map(v => {
      if (v.variant_id !== variantId) return v;
      if (field === 'unit') {
        const cleanedSizeLabel = cleanVariantBaseValue(v.size_label, v.unit);
        return { ...v, unit: value, size_label: cleanedSizeLabel || v.size_label };
      }
      return { ...v, [field]: value };
    }));
  };

  const addAvailabilityTiming = () => {
    setAvailabilityTimings((prev) => [
      ...prev,
      { id: Date.now() + Math.random(), day: 'mon', start: '09:00', end: '21:00' },
    ]);
  };

  const updateAvailabilityTiming = (id, field, value) => {
    setAvailabilityTimings((prev) =>
      prev.map((timing) => (timing.id === id ? { ...timing, [field]: value } : timing))
    );
  };

  const removeAvailabilityTiming = (id) => {
    setAvailabilityTimings((prev) => prev.filter((timing) => timing.id !== id));
  };

  const handleUpdateVariant = async (variant) => {
    if (variant.selling_price === '' || Number(variant.selling_price) <= 0) {
      message.warning('Selling Price must be greater than 0');
      return;
    }
    if (!variant.original_cost || Number(variant.original_cost) <= 0) {
      message.warning('Original Cost must be greater than 0');
      return;
    }
    if (variant.stock_qty !== '' && Number(variant.stock_qty) <= 0) {
      message.warning('Stock Quantity must be greater than 0');
      return;
    }
    setUpdatingVariantId(variant.variant_id);
    try {
      const unitVal = (variant.unit || '').trim();
      const rawVal = cleanVariantBaseValue(variant.value || variant.size_label, unitVal);
      const combinedLabel = formatVariantSizeLabel(rawVal, unitVal);

      const sizeLabelObj = {
        unit: unitVal,
        value: rawVal || combinedLabel,
        size_label: combinedLabel,
      };

      const payload = {
        value: rawVal || combinedLabel,
        size_label: sizeLabelObj,
        size: sizeLabelObj,
        selling_price: variant.selling_price,
        original_cost: variant.original_cost,
        stock_qty: variant.stock_qty,
        unit: variant.unit || '',
        net_weight_unit: variant.unit || '',
        pos_plu: variant.pos_plu || '',
        plu_code: variant.pos_plu || '',
        plu: variant.pos_plu || '',
        gst: typeof variant.gst === 'object' && variant.gst !== null ? variant.gst.id : variant.gst,
        is_active: variant.is_active,
        status: variant.is_active ? '1' : '0',
        is_visible: variant.is_visible !== false ? '1' : '0',
        is_visible_counter: variant.is_visible_counter !== false ? '1' : '0',
        is_visible_at_counter: variant.is_visible_counter !== false ? '1' : '0',
      };
      const response = await AdminService.updateMenuVariant(variant.variant_id, payload);
      if (response && (response.success || response.status === 'success')) {
        message.success('Variant updated successfully');
        if (onRefresh) onRefresh();
        onClose();
      } else {
        message.error('Failed to update variant: ' + (response?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating variant:', error);
      message.error('Error updating variant');
    } finally {
      setUpdatingVariantId(null);
    }
  };

  const handleNewVariantChange = (field, value) => {
    setNewVariant(prev => ({ ...prev, [field]: value }));
  };

  const handleAddVariant = async () => {
    if (!newVariant.size_label) {
      message.warning('Size label is required');
      return;
    }
    if (newVariant.selling_price === '' || Number(newVariant.selling_price) <= 0) {
      message.warning('Selling Price must be greater than 0');
      return;
    }
    if (!newVariant.original_cost || Number(newVariant.original_cost) <= 0) {
      message.warning('Original Cost must be greater than 0');
      return;
    }
    if (newVariant.stock_qty !== '' && Number(newVariant.stock_qty) <= 0) {
      message.warning('Stock Quantity must be greater than 0');
      return;
    }
    setIsAddingVariant(true);
    try {
      const unitVal = (newVariant.unit || '').trim();
      const rawVal = cleanVariantBaseValue(newVariant.value || newVariant.size_label, unitVal);
      const combinedLabel = formatVariantSizeLabel(rawVal, unitVal);

      const sizeLabelObj = {
        unit: unitVal,
        value: rawVal || combinedLabel,
        size_label: combinedLabel,
      };

      const payload = {
        menu_item_id: itemId,
        value: rawVal || combinedLabel,
        size_label: sizeLabelObj,
        size: sizeLabelObj,
        selling_price: newVariant.selling_price || '0',
        original_cost: newVariant.original_cost || '0',
        stock_qty: newVariant.stock_qty || 0,
        unit: newVariant.unit || '',
        net_weight_unit: newVariant.unit || '',
        pos_plu: newVariant.pos_plu || '',
        plu_code: newVariant.pos_plu || '',
        plu: newVariant.pos_plu || '',
        gst: newVariant.gst || '0',
        is_active: newVariant.is_active,
        is_visible: newVariant.is_visible !== false ? '1' : '0',
        is_visible_counter: newVariant.is_visible_counter !== false ? '1' : '0',
        is_visible_at_counter: newVariant.is_visible_counter !== false ? '1' : '0',
      };
      const response = await AdminService.addMenuVariant(payload);
      if (response && (response.success || response.status === 'success')) {
        message.success('Variant added successfully');
        if (onRefresh) onRefresh();
        onClose();
      } else {
        message.error('Failed to add variant: ' + (response?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error adding variant:', error);
      message.error('Error adding variant');
    } finally {
      setIsAddingVariant(false);
    }
  };

  const handleDeleteVariant = async (variantId) => {
    setDeletingVariantId(variantId);
    try {
      const response = await AdminService.deleteMenuVariant(variantId);
      if (response && (response.success || response.status === 'success')) {
        message.success('Variant deleted successfully');
        if (onRefresh) onRefresh();
        onClose();
      } else {
        message.error('Failed to delete variant: ' + (response?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting variant:', error);
      message.error('Error deleting variant');
    } finally {
      setDeletingVariantId(null);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    if (!formData.item_name) {
      message.warning('Please fill out all required fields (*)');
      return;
    }

    if (variants.length > 0) {
      for (let i = 0; i < variants.length; i++) {
        const v = variants[i];
        if (v.selling_price !== '' && Number(v.selling_price) <= 0) {
          message.warning(`Variant ${i + 1}: Selling Price must be greater than 0`);
          return;
        }
        if (!v.original_cost || Number(v.original_cost) <= 0) {
          message.warning(`Variant ${i + 1}: Original Cost must be greater than 0`);
          return;
        }
        if (v.stock_qty !== '' && Number(v.stock_qty) <= 0) {
          message.warning(`Variant ${i + 1}: Stock Quantity must be greater than 0`);
          return;
        }
      }
    }

    if (!formData.category) {
      message.warning('Please select a Category');
      return;
    }

    const selectedCatObj = apiCategories.find(c => (c.category_name || '').trim().toLowerCase() === (formData.category || '').trim().toLowerCase());
    const subCategories = selectedCatObj?.sub_categories || [];
    if (subCategories.length > 0 && !formData.sub_category) {
      message.warning('Please select a Sub Category');
      return;
    }

    const selectedSubCatObj = subCategories.find(sc => (sc.category_name || '').trim().toLowerCase() === (formData.sub_category || '').trim().toLowerCase());

    const payload = new FormData();
    payload.append('item_id', itemId);
    payload.append('item_name', formData.item_name);
    payload.append('sku', formData.sku);
    payload.append('item_category', formData.category);
    payload.append('category_name', formData.category);
    if (selectedCatObj && selectedCatObj.category_id) {
      payload.append('category_id', String(selectedCatObj.category_id));
    }
    payload.append('sub_category', formData.sub_category);
    if (selectedSubCatObj && selectedSubCatObj.category_id) {
      payload.append('sub_category_id', String(selectedSubCatObj.category_id));
    }
    payload.append('item_type', formData.item_type);
    payload.append('preparation_time', formData.preparation_time);
    payload.append('description', formData.description);
    payload.append('status', formData.status);
    payload.append('is_variable', '1');

    payload.append('is_featured', formData.is_featured ? '1' : '0');
    payload.append('is_customizable', formData.is_customizable ? '1' : '0');
    payload.append('is_organic', formData.is_organic ? '1' : '0');

    // Convert status to is_active for backend mapping if necessary
    payload.append('is_active', formData.status === '1' ? 'true' : 'false');

    if (imageFile) {
      payload.append('item_image', imageFile);
    }

    if (useBusinessHours) {
      if (businessHoursData) {
        payload.append('availability_timings', typeof businessHoursData === 'string' ? businessHoursData : JSON.stringify(businessHoursData));
      }
      payload.append('use_business_hours', '1');
    } else {
      if (availabilityTimings.length > 0) {
        const timingsObject = availabilityTimings.reduce((acc, timing) => {
          if (timing.day && timing.start && timing.end) {
            const dayLower = timing.day.toLowerCase();
            if (!acc[dayLower]) {
              acc[dayLower] = [];
            }
            acc[dayLower].push({ open: timing.start, close: timing.end });
          }
          return acc;
        }, {});
        if (Object.keys(timingsObject).length > 0) {
          payload.append('availability_timings', JSON.stringify(timingsObject));
        }
      }
      payload.append('use_business_hours', '0');
    }

    onSave(payload);
  };

  // Inline styles similar to AddNewMenuItemModal
  const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', zIndex: 1000, overflowY: 'auto', padding: '2rem 1rem' };
  const modalStyle = { backgroundColor: '#fff', borderRadius: '12px', width: '100%', maxWidth: '820px', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', position: 'relative' };
  const headerStyle = { padding: '1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
  const titleStyle = { margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#1e293b' };
  const closeButtonStyle = { background: 'none', border: 'none', color: '#64748b', fontSize: '1.25rem', cursor: 'pointer', padding: '0.25rem' };
  const bodyStyle = { padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' };
  const rowStyle = { display: 'flex', gap: '1rem', alignItems: 'flex-start' };
  const inputGroupStyle = { display: 'flex', flexDirection: 'column', gap: '0.375rem', flex: 1 };
  const labelStyle = { fontSize: '0.875rem', fontWeight: 500, color: '#475569', textAlign: 'left' };
  const inputStyle = { padding: '0.625rem 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', color: '#1e293b', outline: 'none', width: '100%', boxSizing: 'border-box' };
  const imageUploadAreaStyle = { border: '2px dashed #cbd5e1', borderRadius: '8px', padding: '1.5rem', textAlign: 'center', cursor: 'pointer', backgroundColor: '#f8fafc', position: 'relative', minHeight: '120px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' };
  const previewImageStyle = { width: '100%', height: '100%', objectFit: 'contain', position: 'absolute', top: 0, left: 0, borderRadius: '8px' };
  const footerStyle = { padding: '1.25rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', backgroundColor: '#f8fafc', borderRadius: '0 0 12px 12px' };
  const btnCancelStyle = { padding: '0.625rem 1.25rem', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: '#475569', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' };
  const btnSaveStyle = { padding: '0.625rem 1.5rem', borderRadius: '8px', border: 'none', backgroundColor: '#3b82f6', color: '#fff', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' };

  if (isLoading) {
    return (
      <div style={overlayStyle}>
        <div style={{ ...modalStyle, padding: '3rem', alignItems: 'center', justifyContent: 'center' }}>
          <p>Loading item details...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={overlayStyle}>
        <div style={modalStyle}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>Edit Menu Item</h2>
            <button style={closeButtonStyle} onClick={onClose}>
              <FaTimes />
            </button>
          </div>

          <div style={bodyStyle}>
            <div style={rowStyle}>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Item Name *</label>
                <input style={inputStyle} name="item_name" value={formData.item_name} onChange={handleChange} placeholder="e.g., Veg Burger" />
              </div>
              {(() => {
                const selectedCategoryObj = apiCategories.find(
                  c => (c.category_name || '').trim().toLowerCase() === (formData.category || '').trim().toLowerCase()
                );
                const categoryValue = selectedCategoryObj ? selectedCategoryObj.category_name : formData.category;
                const availableSubCategories = selectedCategoryObj?.sub_categories || [];
                const selectedSubCatObj = availableSubCategories.find(
                  sc => (sc.category_name || '').trim().toLowerCase() === (formData.sub_category || '').trim().toLowerCase()
                );
                const subCategoryValue = selectedSubCatObj ? selectedSubCatObj.category_name : formData.sub_category;
                const isSubCategoryRequired = availableSubCategories.length > 0;

                return (
                  <>
                    <div style={inputGroupStyle}>
                      <label style={labelStyle}>Category *</label>
                      <select style={inputStyle} name="category" value={categoryValue} onChange={handleChange}>
                        <option value="" disabled>Select Category</option>
                        {apiCategories.map((c, i) => <option key={i} value={c.category_name}>{c.category_name}</option>)}
                      </select>
                    </div>
                    <div style={inputGroupStyle}>
                      <label style={labelStyle}>
                        Sub Category {isSubCategoryRequired ? '*' : ''}
                      </label>
                      <select style={inputStyle} name="sub_category" value={subCategoryValue} onChange={handleChange}>
                        <option value="">Select Sub Category</option>
                        {availableSubCategories.map((sc, i) => (
                          <option key={i} value={sc.category_name}>{sc.category_name}</option>
                        ))}
                      </select>
                    </div>
                  </>
                );
              })()}
            </div>

            <div style={rowStyle}>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Item Type</label>
                <select style={inputStyle} name="item_type" value={formData.item_type} onChange={handleChange}>
                  <option value="Veg">Veg</option>
                  <option value="Non-Veg">Non-Veg</option>
                  <option value="Egg">Egg</option>
                  <option value="Vegan">Vegan</option>
                </select>
              </div>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Status</label>
                <select style={inputStyle} name="status" value={formData.status} onChange={handleChange}>
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Prep Time (mins)</label>
                <input style={inputStyle} name="preparation_time" type="number" value={formData.preparation_time} onChange={handleChange} placeholder="e.g., 15" />
              </div>
            </div>

            <div style={rowStyle}>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Description (Optional)</label>
                <textarea
                  style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Brief description of the item"
                />
              </div>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Item Image</label>
                <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageChange} style={{ display: 'none' }} />
                <div style={imageUploadAreaStyle} onClick={handleImageClick}>
                  {imagePreview ? (
                    <>
                      <img src={imagePreview} alt="Preview" style={previewImageStyle} />
                      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.opacity = 1} onMouseLeave={(e) => e.currentTarget.style.opacity = 0}>
                        <span style={{ color: '#fff', fontSize: '0.875rem', fontWeight: 500 }}><FaCloudUploadAlt style={{ marginRight: '6px' }} />Change Image</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <FaCloudUploadAlt style={{ fontSize: '2rem', color: '#94a3b8', marginBottom: '0.5rem' }} />
                      <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Click to upload image</span>
                      <span style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.25rem' }}>JPG, PNG, WebP (Max 2MB)</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div style={{ width: '100%', padding: '16px 0', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', margin: '10px 0' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#111827', marginBottom: '16px' }}>
                  PRODUCT OPTIONS
                </div>
                <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '30px', justifyContent: 'flex-start', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Switch checked={formData.is_featured} onChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))} size="small" />
                    <span style={{ fontSize: '14px', color: '#374151' }}>Featured</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Switch checked={formData.is_customizable} onChange={(checked) => setFormData(prev => ({ ...prev, is_customizable: checked }))} size="small" />
                    <span style={{ fontSize: '14px', color: '#374151' }}>Customizable</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Switch checked={formData.is_organic} onChange={(checked) => setFormData(prev => ({ ...prev, is_organic: checked }))} size="small" />
                    <span style={{ fontSize: '14px', color: '#374151' }}>Organic</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Switch checked={useBusinessHours} onChange={setUseBusinessHours} size="small" />
                    <span style={{ fontSize: '14px', color: '#374151' }}>Use Business Hours</span>
                  </div>
                </div>
              </div>
            </div>

            {!useBusinessHours && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <label style={{ ...labelStyle, fontSize: '14px', color: '#111827', fontWeight: '600', margin: 0 }}>Availability Timings</label>
                  <button
                    type="button"
                    onClick={addAvailabilityTiming}
                    style={{ background: 'transparent', border: '1px solid #3b82f6', color: '#3b82f6', padding: '6px 16px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '500' }}
                  >
                    <FaPlus size={10} /> Add Timing
                  </button>
                </div>

                {availabilityTimings.length === 0 ? (
                  <div style={{ border: '1px dashed #d1d5db', borderRadius: '8px', padding: '20px', background: '#f9fafb', color: '#6b7280' }}>
                    <p style={{ margin: 0 }}>No availability timings added.</p>
                    <p style={{ margin: '6px 0 0', fontSize: '12px' }}>Click "Add Timing" to configure when this item is available.</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {availabilityTimings.map((timing) => (
                      <div key={timing.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 32px', gap: '12px', alignItems: 'center' }}>
                        <select style={inputStyle} value={timing.day?.toLowerCase()} onChange={(e) => updateAvailabilityTiming(timing.id, 'day', e.target.value)}>
                          <option value="mon">Mon</option>
                          <option value="tue">Tue</option>
                          <option value="wed">Wed</option>
                          <option value="thu">Thu</option>
                          <option value="fri">Fri</option>
                          <option value="sat">Sat</option>
                          <option value="sun">Sun</option>
                        </select>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Open:</span>
                          <input type="time" style={{ ...inputStyle, flex: 1 }} value={timing.start} onChange={(e) => updateAvailabilityTiming(timing.id, 'start', e.target.value)} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Close:</span>
                          <input type="time" style={{ ...inputStyle, flex: 1 }} value={timing.end} onChange={(e) => updateAvailabilityTiming(timing.id, 'end', e.target.value)} />
                        </div>
                        <button type="button" onClick={() => removeAvailabilityTiming(timing.id)} style={{ background: 'transparent', border: 'none', color: '#ef4444', padding: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Remove Timing">
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: '1.5rem', borderTop: '1px solid #e2e8f0', paddingTop: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b', margin: 0 }}>Variants</h3>
                <button
                  style={{ background: 'transparent', border: '1px solid #3b82f6', color: '#3b82f6', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}
                  onClick={() => setShowNewVariantRow(true)}
                  disabled={showNewVariantRow}
                >
                  + Add Variant
                </button>
              </div>

              {showNewVariantRow && (
                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>New Variant</span>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer', userSelect: 'none' }}>
                        <Switch
                          size="small"
                          checked={Boolean(newVariant.is_active)}
                          onChange={(checked) => handleNewVariantChange('is_active', checked)}
                        />
                        <span style={{ fontWeight: 500, fontSize: '12px', color: newVariant.is_active ? '#15803d' : '#6b7280' }}>
                          {newVariant.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </label>
                      <button
                        style={{ padding: '0.35rem 0.85rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500 }}
                        onClick={handleAddVariant}
                        disabled={isAddingVariant}
                      >
                        {isAddingVariant ? 'Saving...' : 'Save Variant'}
                      </button>
                      <button
                        style={{ padding: '0.35rem 0.75rem', backgroundColor: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}
                        onClick={() => { setShowNewVariantRow(false); setNewVariant({ size_label: '', selling_price: '', original_cost: '', stock_qty: '', gst: '2', is_active: true, is_visible: true, is_visible_counter: true }); }}
                        disabled={isAddingVariant}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>

                  {/* Row 1 */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                    <div style={inputGroupStyle}>
                      <label style={labelStyle}>Size Label</label>
                      <input
                        style={inputStyle}
                        value={newVariant.size_label}
                        onChange={(e) => handleNewVariantChange('size_label', e.target.value)}
                        placeholder="e.g., Large"
                      />
                    </div>
                    <div style={inputGroupStyle}>
                      <label style={labelStyle}>Unit</label>
                      <select
                        style={inputStyle}
                        value={newVariant.unit || ''}
                        onChange={(e) => handleNewVariantChange('unit', e.target.value)}
                      >
                        {UNIT_OPTIONS.map((u) => (
                          <option key={u} value={u === 'None' ? '' : u}>{u}</option>
                        ))}
                      </select>
                    </div>
                    <div style={inputGroupStyle}>
                      <label style={labelStyle}>Original Cost (₹) *</label>
                      <input
                        style={inputStyle}
                        type="number"
                        min="0.01"
                        step="any"
                        onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                        value={newVariant.original_cost}
                        onChange={(e) => handleNewVariantChange('original_cost', e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                    <div style={inputGroupStyle}>
                      <label style={labelStyle}>Selling Price (₹) *</label>
                      <input
                        style={inputStyle}
                        type="number"
                        min="0.01"
                        step="any"
                        onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                        value={newVariant.selling_price}
                        onChange={(e) => handleNewVariantChange('selling_price', e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  {/* Row 2 */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', alignItems: 'flex-end' }}>
                    <div style={inputGroupStyle}>
                      <label style={labelStyle}>Stock</label>
                      <input
                        style={inputStyle}
                        type="number"
                        min="1"
                        onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                        value={newVariant.stock_qty}
                        onChange={(e) => handleNewVariantChange('stock_qty', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    <div style={inputGroupStyle}>
                      <label style={labelStyle}>GST (%)</label>
                      <select
                        style={inputStyle}
                        value={newVariant.gst}
                        onChange={(e) => handleNewVariantChange('gst', e.target.value)}
                      >
                        {taxSlabs.length > 0 ? (
                          taxSlabs.map(slab => (
                            <option key={slab.id} value={slab.id}>{slab.tax_name}</option>
                          ))
                        ) : (
                          <>
                            <option value="1">0%</option>
                            <option value="2">5%</option>
                            <option value="3">12%</option>
                            <option value="4">18%</option>
                            <option value="5">28%</option>
                          </>
                        )}
                      </select>
                    </div>
                    <div style={inputGroupStyle}>
                      <label style={labelStyle}>POS PLU</label>
                      <input
                        style={inputStyle}
                        value={newVariant.pos_plu || ''}
                        onChange={(e) => handleNewVariantChange('pos_plu', e.target.value)}
                        placeholder="Optional PLU code"
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center', paddingBottom: '10px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        <Switch
                          size="small"
                          checked={newVariant.is_visible !== false}
                          onChange={(checked) => handleNewVariantChange('is_visible', checked)}
                        />
                        Visible
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        <Switch
                          size="small"
                          checked={newVariant.is_visible_counter !== false}
                          onChange={(checked) => handleNewVariantChange('is_visible_counter', checked)}
                        />
                        Visible Counter
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {variants.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {variants.map((v, i) => {
                    const sizeLabelStr = typeof v.size_label === 'object' && v.size_label !== null
                      ? (v.size_label.value || v.size_label.label || v.size_label.size_label || '')
                      : (v.size_label || '');

                    return (
                      <div key={v.variant_id || i} style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>
                            Variant {i + 1} {sizeLabelStr ? `(${sizeLabelStr})` : ''}
                          </span>
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer', userSelect: 'none' }}>
                              <Switch
                                size="small"
                                checked={Boolean(v.is_active)}
                                onChange={(checked) => handleVariantChange(v.variant_id, 'is_active', checked)}
                              />
                              <span style={{ fontWeight: 500, fontSize: '12px', color: v.is_active ? '#15803d' : '#6b7280' }}>
                                {v.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </label>
                            <button
                              style={{ padding: '0.35rem 0.75rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '5px' }}
                              onClick={() => handleUpdateVariant(v)}
                              disabled={updatingVariantId === v.variant_id}
                              title="Update Variant"
                            >
                              {updatingVariantId === v.variant_id ? '...' : <><SyncGearIcon size={12} /> Update</>}
                            </button>
                            <button
                              style={{ padding: '0.35rem 0.6rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              onClick={() => setConfirmDeleteVariantId(v.variant_id)}
                              disabled={deletingVariantId === v.variant_id}
                              title="Delete Variant"
                            >
                              {deletingVariantId === v.variant_id ? '...' : <FiTrash2 size={14} />}
                            </button>
                          </div>
                        </div>

                        {/* Row 1 */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                          <div style={inputGroupStyle}>
                            <label style={labelStyle}>Size Label</label>
                            <input
                              style={inputStyle}
                              value={sizeLabelStr}
                              onChange={(e) => handleVariantChange(v.variant_id, 'size_label', e.target.value)}
                              placeholder="Size Label"
                            />
                          </div>
                          <div style={inputGroupStyle}>
                            <label style={labelStyle}>Unit</label>
                            <select
                              style={inputStyle}
                              value={v.unit || ''}
                              onChange={(e) => handleVariantChange(v.variant_id, 'unit', e.target.value)}
                            >
                              {UNIT_OPTIONS.map((u) => (
                                <option key={u} value={u === 'None' ? '' : u}>{u}</option>
                              ))}
                            </select>
                          </div>
                          <div style={inputGroupStyle}>
                            <label style={labelStyle}>Original Cost (₹) *</label>
                            <input
                              style={inputStyle}
                              type="number"
                              min="0.01"
                              step="any"
                              onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                              value={v.original_cost ?? ''}
                              onChange={(e) => handleVariantChange(v.variant_id, 'original_cost', e.target.value)}
                              placeholder="0.00"
                            />
                          </div>
                          <div style={inputGroupStyle}>
                            <label style={labelStyle}>Selling Price (₹) *</label>
                            <input
                              style={inputStyle}
                              type="number"
                              min="0.01"
                              step="any"
                              onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                              value={v.selling_price ?? ''}
                              onChange={(e) => handleVariantChange(v.variant_id, 'selling_price', e.target.value)}
                              placeholder="0.00"
                            />
                          </div>
                        </div>

                        {/* Row 2 */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', alignItems: 'flex-end' }}>
                          <div style={inputGroupStyle}>
                            <label style={labelStyle}>Stock</label>
                            <input
                              style={inputStyle}
                              type="number"
                              min="1"
                              onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                              value={v.stock_qty ?? ''}
                              onChange={(e) => handleVariantChange(v.variant_id, 'stock_qty', e.target.value)}
                              placeholder="0"
                            />
                          </div>
                          <div style={inputGroupStyle}>
                            <label style={labelStyle}>GST (%)</label>
                            <select
                              style={inputStyle}
                              value={typeof v.gst === 'object' && v.gst !== null ? v.gst.id : (v.gst || '2')}
                              onChange={(e) => handleVariantChange(v.variant_id, 'gst', e.target.value)}
                            >
                              {taxSlabs.length > 0 ? (
                                taxSlabs.map(slab => (
                                  <option key={slab.id} value={slab.id}>{slab.tax_name}</option>
                                ))
                              ) : (
                                <>
                                  <option value="1">0%</option>
                                  <option value="2">5%</option>
                                  <option value="3">12%</option>
                                  <option value="4">18%</option>
                                  <option value="5">28%</option>
                                </>
                              )}
                            </select>
                          </div>
                          <div style={inputGroupStyle}>
                            <label style={labelStyle}>POS PLU</label>
                            <input
                              style={inputStyle}
                              value={v.pos_plu || ''}
                              onChange={(e) => handleVariantChange(v.variant_id, 'pos_plu', e.target.value)}
                              placeholder="Optional PLU code"
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', paddingBottom: '10px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              <Switch
                                size="small"
                                checked={v.is_visible !== false}
                                onChange={(checked) => handleVariantChange(v.variant_id, 'is_visible', checked)}
                              />
                              Visible
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              <Switch
                                size="small"
                                checked={v.is_visible_counter !== false}
                                onChange={(checked) => handleVariantChange(v.variant_id, 'is_visible_counter', checked)}
                              />
                              Visible Counter
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                !showNewVariantRow && (
                  <p style={{ color: '#64748b', fontSize: '0.875rem' }}>No variants available for this item.</p>
                )
              )}
            </div>
          </div>

          <div style={footerStyle}>
            <button style={btnCancelStyle} onClick={onClose} disabled={isSaving}>Cancel</button>
            <button style={btnSaveStyle} onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {confirmDeleteVariantId && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '400px', width: '90%', textAlign: 'center', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <FiTrash2 style={{ fontSize: '2rem', color: '#ef4444', marginBottom: '0.75rem' }} />
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.125rem', color: '#1e293b' }}>Delete Variant?</h3>
            <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '0.75rem' }}>
              Are you sure you want to delete this variant?
            </p>
            <div style={{ backgroundColor: '#fef9c3', border: '1px solid #fde047', borderRadius: '8px', padding: '0.75rem', marginBottom: '1.5rem', textAlign: 'left' }}>
              <p style={{ color: '#854d0e', fontSize: '0.8rem', margin: 0, fontWeight: 500 }}>
                ⚠️ Warning: This variant may be linked to existing orders, cart items, or other records. Deleting it could cause errors or data inconsistencies in those areas. Proceed only if you are sure this variant is no longer in use. May leads to app crash.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button
                style={{ padding: '0.5rem 1.25rem', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: '#475569', fontSize: '0.875rem', cursor: 'pointer' }}
                onClick={() => setConfirmDeleteVariantId(null)}
                disabled={deletingVariantId === confirmDeleteVariantId}
              >
                Cancel
              </button>
              <button
                style={{ padding: '0.5rem 1.25rem', borderRadius: '8px', border: 'none', backgroundColor: '#ef4444', color: '#fff', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}
                onClick={() => { handleDeleteVariant(confirmDeleteVariantId); setConfirmDeleteVariantId(null); }}
                disabled={deletingVariantId === confirmDeleteVariantId}
              >
                {deletingVariantId === confirmDeleteVariantId ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditMenuItemModal;
